import React from 'react';
import { FileText } from 'lucide-react';
import PublicHeader from '../components/common/PublicHeader';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      <div className="py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-8">
        {/* Header */}
        <div className="mb-8 flex items-start gap-3">
          <FileText className="h-8 w-8 text-yellow-600 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
            <p className="text-gray-600 mt-1">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-sm max-w-none space-y-6">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Agreement to Terms</h2>
            <p className="text-gray-700">
              These Terms of Service ("Terms") constitute a legally binding agreement between you and JustPractice ("Company," "we," "us," "our"). By accessing or using JustPractice's legal case management platform and related services (the "Service"), you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
            </p>
          </section>

          {/* Use License */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Use License</h2>
            <p className="text-gray-700">
              We grant you a non-exclusive, non-transferable, revocable license to use the Service, subject to these Terms and our Privacy Policy. This license permits you to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Create and manage an account</li>
              <li>Access and use the Service for lawful purposes</li>
              <li>Store and manage case information and client data</li>
              <li>Use features such as SMS communications (if enabled)</li>
            </ul>
            <p className="text-gray-700 mt-3">
              This license is personal to you and may not be assigned, transferred, or sublicensed.
            </p>
          </section>

          {/* User Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">3. User Responsibilities</h2>
            <p className="text-gray-700">
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activity that occurs under your account</li>
              <li>Notifying us immediately of any unauthorized access to your account</li>
              <li>Using the Service in compliance with all applicable laws and regulations</li>
              <li>Not using the Service for unlawful or harmful purposes</li>
              <li>Not attempting to gain unauthorized access to the Service</li>
              <li>Ensuring that all information you provide is accurate and complete</li>
              <li>Protecting the confidentiality of client information stored in the Service</li>
            </ul>
          </section>

          {/* SMS Communications Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">4. SMS Communications Terms</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.1 Opt-In Consent</h3>
            <p className="text-gray-700">
              By opting in to SMS communications through our intake form, you explicitly consent to receive SMS text messages from JustPractice. You confirm that:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>You are the owner of the mobile phone number you provide</li>
              <li>You authorize JustPractice to send SMS messages to this number</li>
              <li>You understand that message and data rates may apply</li>
              <li>You are 18 years of age or older</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.2 Message Content</h3>
            <p className="text-gray-700">
              SMS messages from JustPractice will include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Appointment reminders and scheduling updates</li>
              <li>Case status updates and important case information</li>
              <li>Legally required notices and alerts</li>
              <li>Instructions on how to opt out or get help</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>Note:</strong> SMS messages will NOT include marketing or promotional content.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.3 Message Frequency</h3>
            <p className="text-gray-700">
              Message frequency varies significantly based on your case activity and status. During quiet periods, you may receive minimal messages. During active case phases (discovery, hearing preparation, settlement negotiations, etc.), you may receive multiple messages per day. Message frequency is entirely dependent on the activity and urgency of your legal matter and can range from zero to dozens per week during intensive periods.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.4 Message and Data Rates</h3>
            <p className="text-gray-700">
              <strong>Message and data rates may apply.</strong> Standard SMS rates from your mobile carrier will apply to all messages sent and received. Contact your carrier for details about your plan's text messaging rates.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.5 Opting Out</h3>
            <p className="text-gray-700">
              You can opt out of SMS communications at any time by:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Replying STOP to any SMS message you receive</li>
              <li>Contacting us at support@justpractice.com</li>
              <li>Updating your communication preferences in your account settings</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Once you opt out, we will send you a confirmation message and will not send further SMS messages to your number (except as required by law).
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.6 Technical Support</h3>
            <p className="text-gray-700">
              For questions about SMS communications or technical support, reply HELP to any message or contact us at support@justpractice.com.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Intellectual Property Rights</h2>
            <p className="text-gray-700">
              The Service, including all content, features, and functionality (including but not limited to software, design, text, graphics, and logos), is owned by JustPractice or its licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700 mt-3">
              You may not reproduce, distribute, modify, or transmit any content from the Service without our prior written consent.
            </p>
          </section>

          {/* Data Ownership */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Data Ownership and Storage</h2>
            <p className="text-gray-700">
              You retain all rights to data and information you store in the Service (case information, client details, etc.). By storing this information in the Service, you grant JustPractice the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Host and store your data securely</li>
              <li>Access your data to provide and improve the Service</li>
              <li>Use anonymized or aggregated data for analytics and service improvement</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>You remain responsible for maintaining backup copies of important data.</strong> We will not be liable for loss of data due to technical failures, unless caused by our gross negligence.
            </p>
          </section>

          {/* AI and Data Usage */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Artificial Intelligence and Data Usage</h2>
            <p className="text-gray-700">
              JustPractice does not use customer data or client data to train artificial intelligence or machine learning models. Any AI-powered features integrated into the Service operate under strict data isolation:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Customer data and client data are never used to train, fine-tune, or improve AI or machine learning models</li>
              <li>AI features process data only to provide the requested functionality and do not retain data beyond the immediate request</li>
              <li>Third-party AI providers used by the Service are contractually prohibited from using your data for model training</li>
            </ul>
          </section>

          {/* Service Availability and Maintenance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Service Availability and Maintenance</h2>
            <p className="text-gray-700">
              We strive to maintain high availability of the Service. To ensure the quality and security of the platform:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li><strong>Scheduled Maintenance:</strong> We will provide at least seven (7) days' advance notice before any planned maintenance that may affect Service availability. Notices will be communicated via email or in-app notification.</li>
              <li><strong>Emergency Maintenance:</strong> In the event of a critical security vulnerability, data integrity issue, or other emergency requiring immediate action, we may perform maintenance without advance notice. We will notify you as soon as reasonably practicable in such cases.</li>
              <li><strong>Third-Party Services:</strong> The Service relies on third-party infrastructure and integrations. We are not liable for downtime or service interruptions caused by third-party providers.</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Limitation of Liability</h2>
            <p className="text-gray-700">
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>JustPractice shall not be liable for indirect, incidental, special, consequential, or punitive damages</li>
              <li>JustPractice's total liability for any claim shall not exceed the amount you paid (if any) for the Service in the 12 months preceding the claim</li>
              <li>JustPractice is not liable for third-party actions, including SMS delivery failures or carrier issues</li>
            </ul>
            <p className="text-gray-700 mt-3">
              This limitation does not apply to liability for death, personal injury, or fraud.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Disclaimer of Warranties</h2>
            <p className="text-gray-700">
              THE SERVICE IS PROVIDED ON AN "AS-IS" AND "AS-AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. JUSTPRACTICE DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
              <li>Warranties regarding uninterrupted or error-free operation</li>
              <li>Warranties regarding accuracy or completeness of content</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>Note:</strong> JustPractice is not a law firm and does not provide legal advice. Always consult with a qualified attorney regarding your legal matters.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Termination</h2>
            <p className="text-gray-700">
              We may terminate or suspend your account and access to the Service at any time, with or without cause, upon notice. You may terminate your account at any time by contacting us at support@justpractice.com.
            </p>
            <p className="text-gray-700 mt-3">
              Upon termination, you will have ninety (90) days to export your data from the Service. After this 90-day period, we may permanently delete your data. We may also retain certain data as required by law or for legitimate business purposes.
            </p>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify, defend, and hold harmless JustPractice and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Your violation of these Terms</li>
              <li>Your use of the Service</li>
              <li>Your violation of any applicable law or regulation</li>
              <li>Infringement of third-party rights by content you provide</li>
            </ul>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Governing Law</h2>
            <p className="text-gray-700">
              These Terms are governed by and construed in accordance with the laws of the jurisdiction in which JustPractice operates, without regard to its conflict of law principles. You agree to submit to the exclusive jurisdiction of the courts located in that jurisdiction.
            </p>
          </section>

          {/* Entire Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">14. Entire Agreement</h2>
            <p className="text-gray-700">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and JustPractice regarding the Service and supersede all prior and contemporaneous agreements, understandings, and representations.
            </p>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">15. Modifications to Terms</h2>
            <p className="text-gray-700">
              We may modify these Terms at any time. We will notify you of significant changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of the Service after changes constitutes your acceptance of the updated Terms.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">16. Contact Us</h2>
            <p className="text-gray-700">
              If you have questions about these Terms or the Service, please contact us:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
              <p className="text-gray-900 font-semibold">JustPractice Support</p>
              <p className="text-gray-700">Email: support@justpractice.com</p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            These Terms of Service are designed to clearly communicate the rights and responsibilities of users, with special emphasis on SMS communication compliance and data protection.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TermsOfService;
